import { INode } from 'shared/types/tree'
import { NodeType } from 'shared/types/ingestion'
import { getQuestionType, nodeAttribution } from '../../../utility/TreeHelpers'
import { breadthFirstNodeCheck } from 'shared/utils/tree'
import {
  getQuestionIssues,
  IQuestionIssueResolution,
  QuestionIssues,
} from './QuestionCleanup'
import { NodeIdMap } from '../../../version_mapping/utility'

type Mapping = {
  [qid: string]: IQuestionIssueResolution
}

function badQuestionsWithFilter(
  tree: INode,
  filter?: {
    issues?: QuestionIssues[]
    requiresId?: boolean
    fixable?: boolean
  },
) {
  const accumulatedIssues: Mapping = {}
  const satisfiesFilter = (issues: IQuestionIssueResolution) => {
    // no filter, keep it
    if (!filter || !Object.keys(filter).length) return true
    // want fixable but this one is not fixable, toss it
    if (filter.fixable && !issues.fixes.length) {
      return false
    }
    // looking for issues that require ID
    if (
      filter.requiresId !== undefined &&
      filter.requiresId !== issues.requiresId
    ) {
      return false
    }
    if (!filter.issues?.length) return true
    if (
      filter.issues?.length &&
      issues.issues.find((issue) => filter.issues?.includes(issue))
    ) {
      return true
    }
    return false
  }
  const nodeCheck = (node: INode) => {
    if (node.type !== NodeType.Question) return
    const curriculaData = {
      attributionCode: nodeAttribution(tree) || '',
      curriculaTitle: tree.title,
    }
    const issues = getQuestionIssues(node, curriculaData)
    if (issues.issues.length && satisfiesFilter(issues)) {
      accumulatedIssues[node.id] = issues
    }
  }
  breadthFirstNodeCheck(tree, nodeCheck)
  return accumulatedIssues
}

export function getBadQuestions(tree: INode) {
  return badQuestionsWithFilter(tree)
}

export function getQuestionsRequiringManualFix(tree: INode) {
  return badQuestionsWithFilter(tree, {
    issues: [QuestionIssues.RequiresManualFixing],
    requiresId: true,
  })
}

export function sortQuestionIssueCounts(
  idMap: NodeIdMap,
  issues: { [p: string]: IQuestionIssueResolution },
) {
  const { questionTypes, issueCount } = Object.keys(issues).reduce(
    (
      acc: {
        questionTypes: Set<string>
        issueCount: { [issue: string]: number }
      },
      qid,
    ) => {
      acc.questionTypes.add(getQuestionType(idMap[qid].node))
      const issue = issues[qid]
      issue.issues.forEach((qi) => {
        const count = (acc.issueCount[qi] || 0) + 1
        acc.issueCount[qi] = count
      })
      return acc
    },
    { questionTypes: new Set<string>(), issueCount: {} },
  )
  return { issueCount, questionTypes: Array.from(questionTypes) }
}

export async function fixQuestion(
  question: INode,
  fixes: ((q: INode) => Promise<unknown>)[],
) {
  if (!fixes.length) return Promise.resolve()
  return fixes.reduce((acc: Promise<unknown>, fix) => {
    return acc.then(() => fix(question))
  }, Promise.resolve())
}
